import type { VideoTemplate } from "~/common/videoTemplate/types"
import { Empty, Flex, Popover, Tag, Typography } from "antd"
import { ResourcePreview } from "~/components/ResourcePreview"
import { VideoDownloadPolicy } from "~/components/VideoPlayer"
import { getVideoChannelLabel } from "~/common/constants"
import { VideoCoverPreview } from "./VideoCoverPreview"
import { getTemplateBadge } from "~/common/videoTemplate"
import styles from "./index.css"

export const TemplatePreview = ({
  config,
  disabled,
  onCover,
}: {
  config: VideoTemplate
  disabled: boolean
  onCover: (url: string) => void
}) => {
  const badge = getTemplateBadge(config.categories)
  return (
    <Flex
      className={styles.preview}
      vertical
      gap={16}
    >
      <Typography.Text type="secondary">实时预览</Typography.Text>
      {config.videoUrl ? (
        <VideoCoverPreview
          key={config.videoUrl}
          src={config.videoUrl}
          cover={config.videoCover}
          disabled={disabled}
          onCover={onCover}
        />
      ) : (
        <Empty
          description="配置案例视频后在此预览"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
      <Flex
        align="center"
        gap={8}
        wrap
      >
        <Typography.Title
          className={styles.previewTitle}
          level={4}
        >
          {config.title || "模板标题"}
        </Typography.Title>
        {badge && <Tag color={badge === "热门" ? "red" : "green"}>{badge}</Tag>}
      </Flex>
      <Flex
        gap={4}
        wrap
      >
        {config.keywords.map((keyword) => (
          <Tag key={keyword}>#{keyword}</Tag>
        ))}
      </Flex>
      <Typography.Text strong>模板提示词及参考素材</Typography.Text>
      <VideoDownloadPolicy value={true}>
        <Flex
          gap={8}
          wrap
          data-testid="template-preview-resources"
        >
          {config.motionReference?.url && (
            <Popover content="动作参考视频，作为全局动作生成基准，不支持删除或替换">
              <span>
                <ResourcePreview
                  type="video"
                  size="small"
                  src={config.motionReference.url}
                  poster={config.motionReference.cover || undefined}
                  label="动作参考视频"
                />
              </span>
            </Popover>
          )}
          {config.referenceImages.map((image, index) => (
            <ResourcePreview
              key={image.id}
              type="image"
              size="small"
              src={image.url}
              label={image.name.trim() || `图片${index + 1}`}
            />
          ))}
          {config.referenceAudios.map((audio, index) => (
            <ResourcePreview
              key={`audio:${audio.id}`}
              type="audio"
              size="small"
              src={audio.url}
              label={audio.name.trim() || `音频${index + 1}`}
            />
          ))}
        </Flex>
      </VideoDownloadPolicy>
      <Flex
        gap={8}
        wrap
      >
        {config.model && <Tag>{getVideoChannelLabel(config.model)}</Tag>}
        {config.ratio && <Tag>{config.ratio}</Tag>}
        {config.resolution && <Tag>{config.resolution}</Tag>}
        <Tag>{config.duration}s</Tag>
      </Flex>
      {!config.sameStyleAvailable && (
        <Typography.Text type="secondary">
          暂未开放生成
          {config.sameStyleUnavailableReason
            ? `：${config.sameStyleUnavailableReason}`
            : ""}
        </Typography.Text>
      )}
    </Flex>
  )
}
